import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box } from '@mui/material';
import { LexicalEditor } from 'lexical';

import { CasLoaderWithBackdrop, CasReactHookFormDialog } from 'src/compass/shared/components';
import useCasBoolean from 'src/compass/shared/hooks/useCasBoolean';
import { FormMethodType } from 'src/compass/shared/components/CasForm/CasReactHookForm';
import { editorToEmailHtml } from 'src/compass/shared/components/CasLexicalRichTextEditor/lexical-playground/src/CasLexicalRichTextEditor';
import { PENDING_EMAILS_QUERY_KEY } from 'src/compass/shared/components/CasPendingEmails/usePendingEmails';
import useAddMutation from 'src/compass/shared/hooks/reactQuery/useAddMutation';
import useRetrieve from 'src/compass/shared/hooks/reactQuery/useRetrieve';
import { onSuccessEnqueueSnackbar } from 'src/compass/shared/util/onSuccessEnqueueSnackbar';
import { Terms } from 'src/compass/shared/util/terms';
// Direct paths, not the application barrel: this file is inside it, and importing it from
// itself is a cycle (see CasPendingEmailsPill in CasContextProviders).
import CustomerContactsButton from 'src/compass/application/entityMaintenance/CustomerContacts/CustomerContactsButton';
import MessageComposeFields from './MessageComposeFields';
import MySignatureSettingsDialog from './MySignatureSettingsDialog';
import SendAsToggle, { ICON_ONLY_BELOW_MD, LABEL_HIDDEN_BELOW_MD } from './SendAsToggle';
import { composeSchema } from './composeSchema';
import {
  SmsSnapshotType,
  bodyHasContent,
  replaceBodyWithSms,
  smsChangedSince,
  splitBodyForSms,
} from './composeBody';
import SmsToEmailDialog from './SmsToEmailDialog';
import { useConnectRingCentral, useRingCentralConnection } from './ringCentral/useRingCentral';
import {
  imageBudgetProblem,
  imageBytes,
  sourceBytes,
  toTransport,
  toTransportImages,
} from './inlineImages';
import { SmsTextAdjustmentType, makeSmsSpamSafe } from './smsSpamSafeText';
import {
  RecipientOptionType,
  SendAsType,
  SendChannelType,
  addressesFor,
  resolveChannel,
} from './composeMode';

// A pinned Send As, as the Defaults endpoint names the channel. Auto asks for no channel at all.
const CHANNEL_PARAM: Record<SendAsType, string | undefined> = {
  auto: undefined,
  email: 'Email',
  sms: 'Text',
};

interface Props {
  entityId: number;
  onClose: VoidFunction;
}

/*
  Message one customer, from the Customers pages.

  The SCE Reports window composes a report: it opens on a template, carries a data table into the
  body and links data objects out of a text. This one composes nothing - the sender writes what
  they want to say and sends it. So it shares every part that is about the MESSAGE (the recipient
  field, the channel it settles, the schema, the spam-safe rewrite) and none of the machinery that
  is about SCE reports.
*/
export default function CustomerComposeDialog({ entityId, onClose }: Props) {
  const queryClient = useQueryClient();
  const signatureDialog = useCasBoolean();
  const [update, setUpdate] = useState<Date | null>(null);
  const [editor, setEditor] = useState<LexicalEditor | null>(null);
  const [channel, setChannel] = useState<SendChannelType>('email');
  const [sendAs, setSendAs] = useState<SendAsType>('auto');
  const [smsAdjustments, setSmsAdjustments] = useState<SmsTextAdjustmentType[]>([]);
  const [formMethods, setFormMethods] = useState<FormMethodType>(null);
  // A text goes out from the sender's own RingCentral line; until it is connected, Send is off.
  const { connected: ringCentralConnected } = useRingCentralConnection();

  /*
    Addressed on the channel the sender pinned, rather than on the one each contact prefers.

    A default carries one address, so a contact who prefers email arrives as an email address and
    nothing else - and pinning SMS then read as "this contact cannot receive a text" about someone
    with a mobile on file. Asking for the defaults on the channel in play is the same thing Batch
    Send does when a row is pinned. On Auto there is no channel to impose and the contacts answer
    for themselves.
  */
  const { isLoading, isRefetching, data, isError, error, refetch } = useRetrieve({
    url: `Utilities/CustomerMessage/Defaults/${entityId}`,
    params: { channel: CHANNEL_PARAM[sendAs] },
    enabled: !!entityId,
    /*
      The last defaults stay on screen while a pinned channel's load. Without this, each new
      channel is a new query with no data, `isLoading` returns the loader below in place of the
      whole window, and the form comes back empty - which threw away everything written so far,
      Body, Subject, text and pictures, on the first switch to each channel.
    */
    placeholderData: (previous) => previous,
  });

  const onQueued = (response: any) => {
    // Keep the toast: a send often settles to "Sent" before this refetch lands, in which case
    // there is nothing pending and the pill never appears - the toast is the only confirmation
    // those (common, fast) sends ever get.
    onSuccessEnqueueSnackbar(response);
    queryClient.invalidateQueries({ queryKey: [PENDING_EMAILS_QUERY_KEY] });
    onClose();
  };

  // A send refused because RingCentral is not connected starts connecting when OK is pressed.
  const { connectIfNotConnected } = useConnectRingCentral();

  const { mutate: send } = useAddMutation({
    url: 'Utilities/CustomerMessage/Send',
    onSuccess: onQueued,
    onErrorDismiss: connectIfNotConnected,
  });

  /*
    What SMS mode was filled with when it opened, or null while the window is on Email. Kept so
    that leaving it can tell whether the sender changed anything there.
  */
  const smsDerived = useRef<SmsSnapshotType | null>(null);

  // SMS mode as the sender left it, while they are asked whether it replaces the email Body.
  const [smsToEmail, setSmsToEmail] = useState<SmsSnapshotType | null>(null);

  /*
    The rich text body is the one the message is kept in, so SMS mode is filled by converting it
    on the way in - rather than on every keystroke, which keeps the text editable once it is
    showing. On the way back out, changes made there are not simply dropped: an empty email Body
    takes them over, and one with content asks the sender first (SmsToEmailDialog).
  */
  useEffect(() => {
    if (!editor || !formMethods) return;

    if (channel === 'sms') {
      // Already showing: the text is the sender's now, and re-deriving would undo their edits.
      if (smsDerived.current) return;

      const { text, images } = splitBodyForSms(editor);

      // Not validated: this is the window filling its own field, not the sender typing, and an
      // empty body is exactly what it derives when SMS is picked before anything has been written.
      // Validating here answered "Message body is required" the moment the window opened. Send
      // validates, which is when the sender has actually claimed to be finished.
      formMethods.setValue('textContent', text, { shouldValidate: false });
      // The body's pictures, which the text carries as attachments - listed under it, removable.
      formMethods.setValue('smsImages', images, { shouldValidate: false });
      smsDerived.current = { text, images };
      return;
    }

    const derived = smsDerived.current;
    if (!derived) return;
    smsDerived.current = null;

    const sms: SmsSnapshotType = {
      text: formMethods.getValues('textContent') ?? '',
      images: formMethods.getValues('smsImages') ?? [],
    };

    if (!smsChangedSince(sms, derived)) return;

    // Nothing in the email to lose: the SMS content simply becomes it.
    if (bodyHasContent(editor)) setSmsToEmail(sms);
    else {
      replaceBodyWithSms(editor, sms);
      syncContentFromEditor();
    }
    // syncContentFromEditor is a render-scoped function over the same editor and form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, editor, formMethods]);

  // The sender's answer: Yes replaces the email Body with the SMS content; No leaves it as it was.
  const answerSmsToEmail = (replace: boolean) => {
    if (replace && editor && smsToEmail) {
      replaceBodyWithSms(editor, smsToEmail);
      syncContentFromEditor();
    }

    setSmsToEmail(null);
  };

  const onSendAsChange = (value: SendAsType) => {
    setSendAs(value);
    formMethods?.setValue('sendAs', value, { shouldValidate: true });
  };

  /*
    Push the body into the form. The editor reports only what the sender changes, so text typed
    before the field was ever touched would leave it empty and Send would answer "Email body is
    required". Deferred a tick because the editor's update is queued.
  */
  const syncContentFromEditor = () => {
    if (!editor || !formMethods) return;

    setTimeout(
      () => formMethods.setValue('content', editorToEmailHtml(editor), { shouldValidate: false }),
      0
    );
  };

  const onSubmit = async (payload: any) => {
    const {
      category,
      sendAs: submittedSendAs,
      to,
      cc,
      subject,
      content,
      textContent,
      smsImages,
    } = payload;

    // Recomputed from the submitted values rather than trusted from component state, so what is
    // validated is what is sent.
    const sendOn = resolveChannel(submittedSendAs, to, cc);
    const addresses = (list: RecipientOptionType[]) => addressesFor(list, sendOn);
    const isSms = sendOn === 'sms';

    // The button is disabled too; this covers a connection that lapsed while the window was open.
    if (isSms && !ringCentralConnected) return;

    if (isSms) {
      const safe = makeSmsSpamSafe(textContent ?? '');

      /*
        Rewrite, then stop. The sender sees what changed and sends again, rather than finding out
        their words were edited only once the text had gone out.
      */
      if (safe.adjustments.length) {
        formMethods?.setValue('textContent', safe.text, { shouldValidate: true });
        setSmsAdjustments(safe.adjustments);
        return;
      }
    }

    // The server refuses the same thing, but only once asked; here it lands on the field whose
    // pictures they are, before anything is queued.
    const tooMany = imageBudgetProblem(
      isSms ? sourceBytes(smsImages ?? []) : imageBytes(content),
      sendOn
    );

    if (tooMany) {
      const message = isSms ? 'text' : 'email';

      formMethods?.setError(isSms ? 'textContent' : 'content', {
        type: 'manual',
        message: `This ${message} has ${tooMany}. Remove an image or paste a smaller one.`,
      });
      return;
    }

    // An email names its pictures in the body; a text's list is simply what it attaches.
    const email = isSms ? null : await toTransport([content as string]);

    send({
      entityId,
      category,
      channel: isSms ? 'Text' : 'Email',
      subject: isSms ? null : subject,
      content: isSms ? textContent : email!.bodies[0],
      to: addresses(to),
      cc: addresses(cc),
      images: isSms ? await toTransportImages(smsImages ?? []) : email!.images,
    });
  };

  if (isLoading) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error(error.message);

  const isSms = channel === 'sms';

  return (
    <>
      {isRefetching && <CasLoaderWithBackdrop />}
      <CasReactHookFormDialog
        open
        fullScreen
        onClose={onClose}
        setFormMethods={setFormMethods}
        title={
          <>
            {`Compose: ${data?.customerName ?? ''}`}
            <SendAsToggle value={sendAs} onChange={onSendAsChange} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CustomerContactsButton
                entityId={entityId}
                label={`Edit ${Terms.Customer.label} Contacts`}
                onChanged={() => {
                  refetch();
                  setUpdate(new Date());
                }}
                sx={ICON_ONLY_BELOW_MD}
                labelSx={LABEL_HIDDEN_BELOW_MD}
              />
            </Box>
          </>
        }
        schema={composeSchema}
        // No default content: this window has nothing to say on the sender's behalf. No default
        // Category either - picking one is the sender's call (see RHFMessageCategory).
        defaultValues={{ category: null, sendAs: 'auto', subject: '', content: '', ...data }}
        onSubmit={onSubmit}
        submitButtonLabel={isSms ? 'Send Text' : 'Send'}
        buttonProps={{ disabled: isSms && !ringCentralConnected }}
      >
        <MessageComposeFields
          optionsEntity="Utilities/CustomerMessage/Email/Select"
          optionsParams={{ entityId }}
          selectedTo={data?.to}
          selectedCC={data?.cc}
          update={update}
          onReadyEditor={setEditor}
          onChannelChange={setChannel}
          onClearBody={syncContentFromEditor}
          onEditSignature={signatureDialog.setTrue}
          defaultsFollowChannel
          smsAdjustments={smsAdjustments}
          onDismissSmsAdjustments={() => setSmsAdjustments([])}
        />
      </CasReactHookFormDialog>
      {signatureDialog.value && (
        <MySignatureSettingsDialog onClose={signatureDialog.setFalse} />
      )}
      {smsToEmail && (
        <SmsToEmailDialog
          onConfirm={() => answerSmsToEmail(true)}
          onCancel={() => answerSmsToEmail(false)}
        />
      )}
    </>
  );
}
